import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import { parsePlaylistUrl } from '../services/playlistService';
import { ParseResult } from '../types';

interface PlaylistUrlInputProps {
  onSubmit: (result: ParseResult) => void;
  isLoading?: boolean;
  progressMessage?: string;
}

export const PlaylistUrlInput: React.FC<PlaylistUrlInputProps> = ({ onSubmit, isLoading = false, progressMessage }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [detectedPlatform, setDetectedPlatform] = useState<ParseResult | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrl(value);
    setError('');
    setDetectedPlatform(parsePlaylistUrl(value));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!url.trim()) { setError('Please enter a playlist link or ID'); return; }
    const result = parsePlaylistUrl(url);
    if (!result) { setError('Invalid URL. Paste a Spotify, YouTube Music or Apple Music playlist link.'); return; }
    onSubmit(result);
  };

  const isDisabled = isLoading || !detectedPlatform;
  const buttonText = isLoading && progressMessage ? progressMessage : isLoading ? 'Analyzing...' : 'Analyze Playlist';

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '560px', margin: '0 auto' }}>
      <input
        type="text"
        value={url}
        onChange={handleChange}
        placeholder="Paste a Spotify, YouTube Music or Apple Music playlist link..."
        disabled={isLoading}
        style={{
          width: '100%',
          padding: '16px 24px',
          fontSize: '15px',
          border: error ? '2px solid #EF4444' : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)'}`,
          borderRadius: '100px',
          outline: 'none',
          background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
          color: isDark ? '#F8FAFC' : '#0F172A',
          transition: 'all 0.2s ease',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
          marginBottom: '12px',
        }}
        onFocus={(e) => { if (!error) { e.target.style.borderColor = isDark ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; } }}
        onBlur={(e) => { if (!error) { e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)'; e.target.style.boxShadow = 'none'; } }}
      />
      {error && <div style={{ color: '#EF4444', fontSize: '13px', paddingLeft: '24px', marginBottom: '8px' }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
      <button
        type="submit"
        disabled={isDisabled}
        style={{
          width: 'auto',
          padding: '16px 32px',
          fontSize: '15px',
          fontWeight: 600,
          color: isDark && detectedPlatform && !isLoading ? '#000' : '#fff',
          backgroundColor: isLoading ? (isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.6)') : detectedPlatform ? (isDark ? '#fff' : '#000') : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.25)'),
          border: 'none',
          borderRadius: '100px',
          cursor: isLoading ? 'default' : detectedPlatform ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease',
          fontFamily: 'inherit',
          boxShadow: isLoading || !detectedPlatform ? 'none' : '0 4px 16px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
        }}
        onMouseEnter={(e) => { if (!isLoading && detectedPlatform) { e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = isLoading || !detectedPlatform ? 'none' : '0 4px 16px rgba(0,0,0,0.15)'; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {/* Spotify icon */}
        {!isLoading && detectedPlatform?.platform === 'spotify' && (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ animation: 'popIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)' }}>
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
        )}
        {/* YouTube Music icon */}
        {!isLoading && detectedPlatform?.platform === 'ytmusic' && (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ animation: 'popIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)' }}>
            <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L16.2 12l-6.516 3.54z"/>
          </svg>
        )}
        {/* Apple Music icon */}
        {!isLoading && detectedPlatform?.platform === 'apple' && (
          <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor" style={{ animation: 'popIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)' }}>
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
          </svg>
        )}
        {isLoading && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
            <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4m-3.93 7.07l-2.83-2.83M7.76 7.76L4.93 4.93"/>
          </svg>
        )}
        {buttonText}
      </button>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes popIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        button:focus-visible, input:focus-visible { outline: 2px solid #3B82F6; outline-offset: 2px; }
        button:active:not(:disabled) { transform: scale(0.97) !important; }
      `}</style>
    </form>
  );
};
